<template>
  <Dialog
    :visible="modelValue"
    @update:visible="$emit('update:modelValue', $event)"
    modal
    :closable="!isDeleting"
    :close-on-escape="!isDeleting"
    :style="{ width: '32rem' }"
    :pt="{ root: { class: 'rounded-lg' } }"
  >
    <template #header>
      <div class="flex items-center gap-2 text-red-400">
        <span class="material-icons" aria-hidden="true">warning</span>
        <span class="font-semibold">{{ $t('workspace.settings.delete.modalTitle', { name: workspaceName }) }}</span>
      </div>
    </template>

    <div class="space-y-4">
      <div class="bg-red-900/20 border border-red-700/60 rounded-lg p-4">
        <p class="text-red-300 text-sm font-semibold mb-2">{{ $t('workspace.settings.delete.warningHeading') }}</p>
        <ul class="text-sm text-red-200 space-y-1 list-disc list-inside">
          <li>{{ $t('workspace.settings.delete.warningItem1') }}</li>
          <li>{{ $t('workspace.settings.delete.warningItem2') }}</li>
          <li>{{ $t('workspace.settings.delete.warningItem3') }}</li>
          <li>{{ $t('workspace.settings.delete.warningItem4') }}</li>
          <li>{{ $t('workspace.settings.delete.warningItem5') }}</li>
        </ul>
      </div>

      <div>
        <label for="input-delete-workspace-confirm" class="text-sm text-gray-300 block mb-2">
          {{ $t('workspace.settings.delete.confirmLabel') }}
        </label>
        <InputText
          id="input-delete-workspace-confirm"
          v-model="confirmInput"
          :placeholder="$t('workspace.settings.delete.confirmPlaceholder')"
          :disabled="isDeleting"
          class="w-full"
        />
      </div>

      <div v-if="errorMessage" class="bg-red-900/30 border border-red-700/60 rounded-lg p-3 flex items-start gap-2">
        <span class="material-icons text-red-400 text-base">error_outline</span>
        <div class="flex-1">
          <p class="text-red-300 text-sm font-semibold">{{ $t('workspace.settings.delete.errorTitle') }}</p>
          <p class="text-red-300 text-sm">{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        severity="secondary"
        :label="$t('workspace.settings.delete.cancelAction')"
        :disabled="isDeleting"
        @click="$emit('update:modelValue', false)"
      />
      <!-- Icon in #icon (not the default slot) so :loading can swap it for
           PrimeVue's spinner; the default slot would replace the whole content. -->
      <Button
        severity="danger"
        :label="isDeleting ? $t('workspace.settings.delete.deleting') : $t('workspace.settings.delete.confirmAction')"
        :loading="isDeleting"
        :disabled="!canDelete || isDeleting"
        @click="performDelete"
      >
        <template #icon>
          <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">delete_forever</span>
        </template>
      </Button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';

const { t } = useI18n();

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  workspaceId: { type: String, required: true },
  workspaceName: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue', 'deleted']);

const confirmInput = ref('');
const isDeleting = ref(false);
const errorMessage = ref('');

const canDelete = computed(() => confirmInput.value.trim() === props.workspaceName);

watch(() => props.modelValue, (open) => {
  if (open) {
    confirmInput.value = '';
    errorMessage.value = '';
    isDeleting.value = false;
  }
});

const performDelete = async () => {
  if (!canDelete.value) return;
  isDeleting.value = true;
  errorMessage.value = '';
  try {
    const res = await api.delete(`/workspaces/${props.workspaceId}`);
    const warnings = res.data?.warnings || [];
    emit('deleted', { warnings });
    emit('update:modelValue', false);
  } catch (err) {
    errorMessage.value = err.response?.data?.error?.message || t('common.error');
  } finally {
    isDeleting.value = false;
  }
};
</script>
